/**
 * Instants from the API, rendered for a person.
 *
 * Every timestamp `/panel-api/v1` returns is RFC 3339 in UTC, and that is the
 * right wire format: it is unambiguous, sortable and comparable without a
 * timezone database. It is the wrong *display* format. A customer reading their
 * delivery log at 12:15 does not want to see `2026-09-27T10:15:30Z` and work out
 * that this was nine minutes ago — they want to see 12:15, because that is what
 * their own clock says.
 *
 * So the conversion happens here, in the browser, exactly once per rendered
 * value: JavaScript's `Date` already knows the visitor's zone, and the zone is a
 * property of the reader, not of the server or of the request.
 *
 * Two rules this module keeps:
 *
 *   - A value that cannot be parsed is returned **verbatim**. The calendar is
 *     the customer's and the log is a record of what happened; silently dropping
 *     or zeroing a timestamp we did not understand would make the screen lie.
 *   - The raw instant stays reachable. Callers pass it as the element's `title`,
 *     so the exact UTC value the server recorded is one hover away — which is
 *     what you need when comparing a panel row with a server log.
 */

/** The fields a log needs, without a locale-dependent date order assumption. */
const INSTANT_FIELDS = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

/**
 * The zone the reader's clocks are in, as the account states it.
 *
 * Held here rather than threaded through every component: the conversion is the
 * same one everywhere, and a parameter that a hundred call sites must remember
 * to pass is a parameter that a hundred call sites will eventually forget. The
 * browser's zone is the fallback for an anonymous visitor and for the moment
 * before the session loads — it is a better guess than UTC, because it is at
 * least the zone the reader's own machine is showing them.
 */
let accountTimeZone = null;

/**
 * setAccountTimeZone records the zone the account reads times in.
 *
 * Called from the auth provider whenever the session changes, so it also covers
 * signing out: the next customer on the machine must not inherit the last one's
 * clock.
 */
export function setAccountTimeZone(timeZone) {
  accountTimeZone = timeZone || null;
}

/** accountZone is the recorded zone, or undefined to mean "the browser's". */
export function accountZone() {
  return accountTimeZone ?? undefined;
}

/** parseInstant returns the instant, or null when the value is not a date. */
export function parseInstant(value) {
  if (value == null || value === "") return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * formatInstant renders an API timestamp in the reader's timezone.
 *
 * @param value RFC 3339 string from the API. Anything unparseable is returned
 *   unchanged, so a malformed row is visible rather than blank.
 * @param options.locale BCP 47 tag; defaults to the browser's.
 * @param options.timeZone IANA zone; defaults to the browser's. It exists so a
 *   test can assert a conversion without depending on the machine it runs on.
 * @returns e.g. `27 Sep 2026, 12:15:30`.
 */
export function formatInstant(value, { locale, timeZone } = {}) {
  const at = parseInstant(value);
  if (!at) return value == null ? "" : String(value);
  const options = { ...INSTANT_FIELDS };
  const zone = timeZone ?? accountTimeZone;
  if (zone) options.timeZone = zone;
  return at.toLocaleString(locale, options);
}

/**
 * formatDay renders only the calendar day, for rows where the time is secondary.
 */
export function formatDay(value, { locale, timeZone } = {}) {
  const at = parseInstant(value);
  if (!at) return value == null ? "" : String(value);
  const options = { day: "numeric", month: "short", year: "numeric" };
  const zone = timeZone ?? accountTimeZone;
  if (zone) options.timeZone = zone;
  return at.toLocaleDateString(locale, options);
}

/**
 * instantTitle is the tooltip for a formatted instant: the exact value the API
 * sent, labelled, plus the reader's zone so "12:15" is attributable.
 */
export function instantTitle(value, { timeZone } = {}) {
  const at = parseInstant(value);
  if (!at) return value == null ? "" : String(value);
  // The same zone the value is printed in, or the label would attribute a time
  // to a zone the reader is not looking at.
  const zone =
    timeZone || accountTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
  return `${at.toISOString()} (UTC) · shown in ${zone}`;
}
